import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../Context/AuthContext'
import DarkAuthInput from '../../Components/Inputs/DarkAuthInput'
import logo from '../../Assets/new-logo.png'
import { CheckCircle, RefreshCw } from 'react-feather'

const ConfirmEmailScreen = () => {
  const { confirmEmailCode, resetConfirmEmail } = useAuth()
  const [confirmationCode, setConfirmationCode] = useState<string>('')
  const [isResending, setIsResending] = useState<boolean>(false)

  const handleConfirmationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmationCode(e.target.value)
  }

  const checkConfirmationCode = () => {
    confirmEmailCode(confirmationCode)
  }

  const handleResendCode = () => {
    setIsResending(true)
    resetConfirmEmail()
    setTimeout(() => setIsResending(false), 2000)
  }

  return (
    <div className="min-h-screen w-full bg-dark-bg flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background gradient orbs */}
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-accent-purple/20 rounded-full blur-3xl translate-x-1/2 animate-float" />
      <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-accent-gold/15 rounded-full blur-3xl translate-y-1/2 animate-float-slow" />
      <div className="absolute top-0 left-0 w-64 h-64 bg-accent-pink/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 animate-float-slower" />

      {/* Main card */}
      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        {/* Logo and header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-dark-card rounded-2xl border border-dark-border mb-4 animate-bounce-subtle">
            <img className="h-14 w-14 object-contain" src={logo} alt="Logo" />
          </div>
          <h1 className="text-3xl font-black text-white mb-2">Email Confirmation</h1>
          <p className="text-gray-400">Check your email for a confirmation code</p>
        </div>

        {/* Confirmation form card */}
        <div className="bg-dark-card border border-dark-border rounded-2xl p-8 shadow-2xl animate-scale-in">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-accent-purple/20 to-accent-gold/20 rounded-2xl flex items-center justify-center border border-dark-border animate-pulse-slow">
              <CheckCircle className="w-10 h-10 text-accent-teal" />
            </div>
          </div>

          <DarkAuthInput
            value={confirmationCode}
            handleFunction={handleConfirmationCodeChange}
            placeholder="Enter confirmation code"
            type="text"
            icon="password"
          />

          <button
            onClick={checkConfirmationCode}
            disabled={!confirmationCode}
            className="w-full py-3.5 px-4 bg-gradient-to-r from-accent-teal to-accent-deep-purple
                       text-white font-semibold rounded-xl
                       hover:opacity-90 hover:scale-[1.02] transition-all duration-200
                       disabled:opacity-50 disabled:cursor-not-allowed
                       shadow-lg shadow-accent-teal/25 hover:shadow-xl hover:shadow-accent-teal/30 mt-2"
          >
            Confirm Code
          </button>

          {/* Resend code */}
          <button
            onClick={handleResendCode}
            disabled={isResending}
            className="w-full py-3 mt-3 text-accent-teal hover:text-accent-teal/80
                       transition-colors text-sm font-medium flex items-center justify-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isResending ? 'animate-spin' : ''}`} />
            {isResending ? 'Sending...' : 'Resend Code'}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-dark-border" />
            <span className="text-gray-500 text-sm">or</span>
            <div className="flex-1 h-px bg-dark-border" />
          </div>

          {/* Back to login link */}
          <Link to="/" className="block text-center">
            <span className="text-accent-gold hover:text-accent-gold-light transition-colors text-sm font-medium">
              Back to Login
            </span>
          </Link>
        </div>

        {/* Help note */}
        <div className="mt-6 flex justify-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="bg-dark-card/50 rounded-xl px-5 py-2.5 border border-dark-border hover:border-accent-teal/30 transition-colors duration-300">
            <p className="text-gray-500 text-xs text-center">
              The code expires in 10 minutes
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConfirmEmailScreen
